import { Box, Typography, IconButton, keyframes, styled } from '@mui/material'

export const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`

export const float = keyframes`
  0%, 100% { transform: translate(0, 0) scale(1); }
  25% { transform: translate(40px, -40px) scale(1.15); }
  50% { transform: translate(-30px, 30px) scale(0.95); }
  75% { transform: translate(30px, 40px) scale(1.05); }
`

export const particleFloat = keyframes`
  0% { transform: translateY(100vh) translateX(0) scale(0); opacity: 0; }
  10% { opacity: 0.6; transform: translateY(90vh) translateX(10px) scale(1); }
  90% { opacity: 0.6; transform: translateY(10vh) translateX(-10px) scale(1); }
  100% { transform: translateY(-10vh) translateX(20px) scale(0); opacity: 0; }
`

export const breathe = keyframes`
  0%, 100% { transform: scale(1); box-shadow: 0 30px 80px rgba(0, 0, 0, 0.4); }
  50% { transform: scale(1.03); box-shadow: 0 40px 100px rgba(0, 0, 0, 0.5); }
`

export const breatheActive = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
`

export const ripple = keyframes`
  0% { width: 220px; height: 220px; opacity: 1; }
  100% { width: 380px; height: 380px; opacity: 0; }
`

export const pulseText = keyframes`
  0%, 100% { opacity: 0.8; }
  50% { opacity: 1; }
`

export const visualizerIdle = keyframes`
  0%, 100% { opacity: 0.6; transform: scaleY(1); }
  50% { opacity: 1; transform: scaleY(1.2); }
`

export const slideUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`

export const FullscreenOverlay = styled(Box)(({ theme }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 9999,
  background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 50%, #312e81 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  animation: `${fadeIn} 0.5s ease-in-out`,
  overflow: 'hidden',
}))

export const AnimatedBackground = styled(Box)({
  position: 'absolute',
  inset: 0,
  overflow: 'hidden',
  zIndex: 0,
})

export const Orb = styled(Box)(({ size, color, top, left, right, bottom, delay }) => ({
  position: 'absolute',
  width: size,
  height: size,
  borderRadius: '50%',
  background: `linear-gradient(135deg, ${color})`,
  filter: 'blur(100px)',
  opacity: 0.4,
  top: top || 'auto',
  left: left || 'auto',
  right: right || 'auto',
  bottom: bottom || 'auto',
  animation: `${float} 25s ease-in-out infinite`,
  animationDelay: delay,
  willChange: 'transform',

  '@media (max-width: 768px)': {
    width: typeof size === 'number' ? size * 0.7 : `calc(${size} * 0.7)`,
    height: typeof size === 'number' ? size * 0.7 : `calc(${size} * 0.7)`,
    filter: 'blur(80px)',
  },
}))

export const Particle = styled(Box)({
  position: 'absolute',
  width: 3,
  height: 3,
  backgroundColor: 'rgba(255, 255, 255, 0.6)',
  borderRadius: '50%',
  animationName: `${particleFloat}`,
  animationTimingFunction: 'linear',
  animationIterationCount: 'infinite',
  willChange: 'transform, opacity',
})

export const CenterContent = styled(Box)(({ theme }) => ({
  position: 'relative',
  zIndex: 10,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '40px',
  textAlign: 'center',
  maxWidth: '700px',
  padding: '40px',
  [theme.breakpoints.down('md')]: {
    padding: '20px',
    gap: '28px',
  },
}))

export const AvatarContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: 280,
  height: 280,
  [theme.breakpoints.down('md')]: { width: 200, height: 200 },
  [theme.breakpoints.down('sm')]: { width: 180, height: 180 },
}))

export const AvatarCircle = styled(Box, {
  shouldForwardProp: prop => prop !== 'listening',
})(({ listening, theme }) => ({
  width: '100%',
  height: '100%',
  borderRadius: '50%',
  background: 'rgba(255, 255, 255, 0.08)',
  backdropFilter: 'blur(20px)',
  border: '3px solid',
  borderColor: listening ? 'rgba(16, 185, 129, 0.6)' : 'rgba(255, 255, 255, 0.15)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
  boxShadow: listening
    ? '0 30px 80px rgba(16, 185, 129, 0.3), 0 0 60px rgba(16, 185, 129, 0.2)'
    : '0 30px 80px rgba(0, 0, 0, 0.4)',
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  animation: `${listening ? breatheActive : breathe} ${
    listening ? '2s' : '4s'
  } ease-in-out infinite`,

  '& img': {
    width: 200,
    height: 200,
    borderRadius: '50%',
    objectFit: 'cover',
    [theme.breakpoints.down('md')]: { width: 140, height: 140 },
    [theme.breakpoints.down('sm')]: { width: 120, height: 120 },
  },
}))

export const ListeningRing = styled(Box)(({ delay }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  border: '2px solid rgba(16, 185, 129, 0.7)',
  borderRadius: '50%',
  animation: `${ripple} 2.5s cubic-bezier(0.4, 0, 0.2, 1) infinite`,
  animationDelay: delay,
  pointerEvents: 'none',
}))

export const StatusTitle = styled(Typography)(({ theme }) => ({
  color: 'white',
  fontSize: '36px',
  fontWeight: 700,
  letterSpacing: '-0.5px',
  textShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
  margin: 0,
  marginBottom: '12px',
  [theme.breakpoints.down('md')]: { fontSize: '28px' },
  [theme.breakpoints.down('sm')]: { fontSize: '24px' },
}))

export const StatusSubtitle = styled(Typography)(({ theme }) => ({
  color: 'rgba(255, 255, 255, 0.8)',
  fontSize: '18px',
  margin: 0,
  animation: `${pulseText} 3s ease-in-out infinite`,
  [theme.breakpoints.down('md')]: { fontSize: '16px' },
}))

export const ScreenBadge = styled(Box)({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  background: 'rgba(59, 130, 246, 0.2)',
  backdropFilter: 'blur(10px)',
  color: '#60a5fa',
  padding: '8px 16px',
  borderRadius: '20px',
  fontSize: '14px',
  fontWeight: 600,
  border: '1px solid rgba(59, 130, 246, 0.3)',
  marginTop: '8px',
})

export const VisualizerContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '5px',
  height: '100px',
  padding: '20px',
  [theme.breakpoints.down('md')]: { height: '80px', gap: '4px' },
}))

export const VisualizerBar = styled(Box)(({ theme }) => ({
  width: '5px',
  minHeight: '20px',
  background: 'linear-gradient(180deg, #10b981 0%, #059669 100%)',
  borderRadius: '4px',
  transition: 'height 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
  transformOrigin: 'bottom',
  boxShadow: '0 0 10px rgba(16, 185, 129, 0.3)',
  animation: `${visualizerIdle} 2s ease-in-out infinite`,

  '&:nth-of-type(even)': {
    animationDelay: '0.1s',
  },
  [theme.breakpoints.down('md')]: { width: '4px' },
}))

export const ControlsContainer = styled(Box)(({ theme }) => ({
  position: 'fixed',
  bottom: '30px',
  left: '50%',
  transform: 'translateX(-50%)',
  display: 'flex',
  gap: '20px',
  alignItems: 'center',
  background: 'rgba(255, 255, 255, 0.08)',
  backdropFilter: 'blur(30px)',
  padding: '18px 28px',
  borderRadius: '60px',
  border: '1px solid rgba(255, 255, 255, 0.15)',
  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.4)',
  zIndex: 10002,
  [theme.breakpoints.down('md')]: { bottom: '20px', gap: '16px', padding: '14px 22px' },
  [theme.breakpoints.down('sm')]: { bottom: '15px' },
}))

export const ControlBtn = styled(IconButton, {
  shouldForwardProp: prop => prop !== 'variantType' && prop !== 'isActive',
})(({ theme, variantType, isActive }) => {
  let bg = 'transparent'
  let color = 'white'
  let shadow = '0 8px 20px rgba(0, 0, 0, 0.3)'
  let hoverShadow = '0 12px 40px rgba(0, 0, 0, 0.4)'
  let activeShadow = ''

  if (variantType === 'end-call') {
    bg = 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
    shadow = '0 8px 30px rgba(239, 68, 68, 0.5), 0 0 40px rgba(239, 68, 68, 0.3)'
    hoverShadow = '0 12px 50px rgba(239, 68, 68, 0.7), 0 0 50px rgba(239, 68, 68, 0.5)'
  } else if (isActive) {
    bg = 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
    shadow = '0 8px 30px rgba(16, 185, 129, 0.5), 0 0 40px rgba(16, 185, 129, 0.3)'
    activeShadow =
      '0 12px 50px rgba(16, 185, 129, 0.6), 0 0 50px rgba(16, 185, 129, 0.4)'
  } else if (variantType === 'muted') {
    bg = 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)'
  }

  return {
    width: '64px',
    height: '64px',
    borderRadius: '50%',
    background: bg,
    color: color,
    fontSize: '26px',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    position: 'relative',
    overflow: 'hidden',
    boxShadow: shadow,

    '&:hover': {
      transform:
        variantType === 'end-call'
          ? 'scale(1.15) translateY(-3px)'
          : 'scale(1.1) translateY(-2px)',
      boxShadow: activeShadow || hoverShadow,
      background:
        variantType === 'end-call'
          ? 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)'
          : bg,
    },
    '&:active': {
      transform: 'scale(0.95)',
    },

    [theme.breakpoints.down('md')]: { width: '56px', height: '56px', fontSize: '22px' },
    [theme.breakpoints.down('sm')]: { width: '52px', height: '52px', fontSize: '20px' },
  }
})

// Transcript
export const TranscriptWrapper = styled(Box)(({ theme }) => ({
  position: 'fixed',
  bottom: '120px',
  left: '50%',
  transform: 'translateX(-50%)',
  maxWidth: '90%',
  width: 'auto',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '12px',
  zIndex: 10001,
  pointerEvents: 'none',
  [theme.breakpoints.down('md')]: { bottom: '100px', maxWidth: '95%' },
  [theme.breakpoints.down('sm')]: { bottom: '90px' },
}))

export const TranscriptLine = styled(Box)(({ theme }) => ({
  background: 'rgba(0, 0, 0, 0.75)',
  backdropFilter: 'blur(20px)',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  borderRadius: '24px',
  padding: '16px 28px',
  color: 'rgba(255, 255, 255, 0.95)',
  fontSize: '18px',
  lineHeight: 1.5,
  textAlign: 'center',
  maxWidth: '800px',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
  transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
  animation: `${slideUp} 0.4s cubic-bezier(0.4, 0, 0.2, 1)`,

  [theme.breakpoints.down('md')]: {
    fontSize: '16px',
    padding: '12px 20px',
    maxWidth: '95vw',
  },
  [theme.breakpoints.down('sm')]: { fontSize: '14px', padding: '10px 16px' },
}))
